<template>
	<div
		class="income-box"
		v-if="showIncomePanel && !tutorialActive"
		@click="onClick"
	>
		<span
			class="income-box__title"
			:class="{ 'income-box__title--boosted': incomeBoostActive }"
		>
			<AppIconButton
				class="income-box__info"
				:type="IconTypes.INFO"
				:size="12"
				:iconSize="9"
			/>
			<span v-if="incomeBoostActive"
				>{{ INCOME_BOOST_COEFFICIENT }}x</span
			>
			<span>{{ translate("ui.income") }}</span>
		</span>
		<span class="income-box__value">
			{{ formatNumber(animatedValueRef) }}
			<AppMainIcon
				class="income-box__icon"
				iconName="coins"
				:iconSize="16"
			/>
			{{ `/ ${GAME_TICKER_INTERVAL / 1_000}s` }}
		</span>
	</div>
</template>

<script lang="ts" setup>
import AppIconButton from "@/components/AppIconButton.vue";
import AppMainIcon from "@/components/AppMainIcon.vue";
import {
	GAME_TICKER_INTERVAL,
	INCOME_BOOST_COEFFICIENT,
	NUMBER_ANIMATION_DURATION,
} from "@/config/constants";
import useEmitter, { EmitterEvents } from "@/hooks/useEmitter";
import useQuestStore from "@/store/useQuestStore";
import useResourcesStore from "@/store/useResourcesStore";
import useRuntimeStore from "@/store/useRuntimeStore";
import useSettingsStore from "@/store/useSettingsStore";
import useSettlementsStore from "@/store/useSettlementsStore";
import { IconTypes, RouteNames } from "@/types/ui";
import { formatNumber } from "@/utils/format";
import { translate } from "@/utils/translate";
import { storeToRefs } from "pinia";
import { markRaw, shallowRef, watch } from "vue";
// @ts-expect-error - TS7016
import { linear, useTween } from "vue-femtotween";
import { useRouter } from "vue-router";

const emitter = markRaw(useEmitter());
const router = useRouter();

const settingsStore = useSettingsStore();
const { showIncomePanel } = storeToRefs(settingsStore);

const resourcesStore = useResourcesStore();
const { coinsIncome } = storeToRefs(resourcesStore);

const runtimeStore = useRuntimeStore();
const { incomeBoostActive } = storeToRefs(runtimeStore);

const settlementsStore = useSettlementsStore();
const { mainLevel } = storeToRefs(settlementsStore);

const questStore = useQuestStore();
const { tutorialActive } = storeToRefs(questStore);

const valueRef = shallowRef<number>(coinsIncome.value ?? 0);
const animatedValueRef = useTween(valueRef, {
	easing: linear,
	time: NUMBER_ANIMATION_DURATION,
	precision: 0,
});

const onClick = (): void => {
	router.push({ name: RouteNames.INFO, params: { tab: 3 } });
};

watch(
	(): number => coinsIncome.value,
	(): void => {
		if (
			!tutorialActive.value &&
			mainLevel.value > 1 &&
			mainLevel.value < 5
		) {
			emitter.emit(EmitterEvents.SettlementQuestUpdate);
		}
		animatedValueRef.stop();
		animatedValueRef.value = valueRef.value ?? 0;
		valueRef.value = coinsIncome.value ?? 0;
	},
);
</script>

<style lang="scss" scoped>
.income-box {
	@include flex-col-center;
	position: relative;
	align-items: start;
	@include background(
		url($path-backgrounds + "income/income-bg.avif"),
		contain,
		center,
		no-repeat
	);
	cursor: pointer;
	padding: 0.625rem;
	width: 8.25rem;
	height: 3.125rem;

	&__title {
		@include flex-row-center;
		justify-content: flex-start;
		gap: 0.313rem;
		height: 1rem;
		color: #f2dcb5;
		font-weight: 500;
		font-size: 0.813rem;
		line-height: 1rem;

		& > span {
			position: relative;
			padding-bottom: 0.25rem;
		}

		&--boosted {
			color: #19b345;
		}
	}

	&__value {
		@include flex-row-center;
		gap: 0.375rem;
		color: #f2dcb5;
		font-weight: 800;
		font-size: 0.875rem;
		white-space: nowrap;
	}

	&__icon {
		margin-top: 0.25rem;
	}

	&__info {
		position: relative;
	}
	&__tooltip {
		text-align: center;
		&:first-child {
			margin-bottom: 0.25rem;
		}
	}
}
</style>
